


































































































































import Vue from 'vue';
import { Component, Watch, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Validator } from 'vee-validate';

import MutationObserverMixin from '@/shared/mixins/MutationObserverMixin';

import { displayCPF } from '@/helpers/strings';

import AddressComponent from '@/shared/components/Address/Address.vue';
import Button from '@/shared/components/Button/Button.vue';
import Checkbox from '@/shared/components/Inputs/Checkbox.vue';
import EssencialInformation from './components/EssencialInformation.vue';
import Heading from '@/shared/components/Heading/Heading.vue';
import TermsMobileModal from '@/shared/components/Terms/TermsMobileModal.vue';
import PolicyMobileModal from '@/shared/components/Terms/PolicyMobileModal.vue';
import ModalMobile from '@/shared/components/ModalMobile/ModalMobile.vue';
import ReCAPTCHABranding from '@/shared/components/ReCAPTCHABranding/ReCAPTCHABranding.vue';

import Address from '@/classes/Address';
import Customer from '@/classes/Customer';

import { ICustomer } from '@/interfaces/Customer';
import { IZipCodeNotFound } from '@/interfaces/ZipCodeNotFound';
import { ITraceableError } from '@/interfaces/Error';

import Cookie from '@/helpers/cookie';
import { getValueIfIsNotNil } from '@/helpers/values';
import { EPhoneKind, EErrorScope, EErrorExhibition } from '@/helpers/enums';
import { redirectToPolicy } from '@/helpers/redirects';
import { executeCaptcha } from '@/helpers/captcha';

const ErrorModule = namespace('App/Error');
const CustomerModule = namespace('App/Customer');
const BehaviorModule = namespace('App/Behavior');

@Component({
  components: {
    Address: AddressComponent,
    Button,
    Checkbox,
    EssencialInformation,
    Heading,
    TermsMobileModal,
    PolicyMobileModal,
    ModalMobile,
    ReCAPTCHABranding,
  },
})
export default class RegisterMobile extends Mixins(MutationObserverMixin) {
  $refs!: {
    step1: HTMLElement;
    step2: HTMLElement;
  };

  private validator = new Validator();

  private stepPosition: number = 0;

  private stepHeight: string = 'auto';

  private isLoadingFirstStep: boolean = false;

  private modalOpen: boolean = false;

  private form = {
    address: new Address({
      street_name: '',
      street_number: '',
      city: '',
      neighborhood: '',
      state: '',
      postal_code: '',
      ibge_code: '',
    }),
    essencialInformation: {
      name: '',
      email: '',
      document: '',
      password: '',
      phone: '',
    },
    acceptTerms: null,
    errors: this.validator.errors,
    states: {
      termsAndConditions: false,
    },
  };

  @CustomerModule.State('isLoading')
  private isLoading: boolean;

  @CustomerModule.Action('signup')
  private signup!: ({ customer, isEverywhere }: { customer: ICustomer, isEverywhere: boolean })=> void;

  @CustomerModule.Action('notFoundZipCode')
  private notFoundZipCode: (newCustomer: IZipCodeNotFound)=> void;

  @ErrorModule.Getter('errorsByExhibitionAndScope')
  private errorsByExhibitionAndScope: Function;

  @ErrorModule.Mutation('hideErrorsByScope')
  private hideErrorsByScope: Function;

  @BehaviorModule.Getter
  private isCaptchaValid: boolean;

  @BehaviorModule.Action
  private setCaptchaValid: Function;

  private get registerError() {
    const error = this.errorsByExhibitionAndScope(
      EErrorExhibition.alert,
      [EErrorScope.register]
    ).find((error: ITraceableError) => error.visibility);

    return error;
  }

  private get isMobile () {
    return this.$vuetify.breakpoint.smAndDown;
  }

  private clearErrors() {
    return this.hideErrorsByScope(EErrorScope.register);
  }

  private displayCPF(string: string) {
    return displayCPF(string);
  }

  private get customer(): ICustomer {
    return new Customer({
      name: this.form.essencialInformation.name,
      email: this.form.essencialInformation.email,
      document: this.form.essencialInformation.document.replace(/\D/g, ''),
      password: this.form.essencialInformation.password,
      terms_optin: this.form.acceptTerms,
      addresses: [this.form.address],
      phone_numbers: [
        {
          kind: EPhoneKind.mobile,
          phone: `+55${this.form.essencialInformation.phone.replace(/\D/g, '')}`,
          is_default: true,
          is_active: true,
        },
      ],
    });
  }

  private get showSecond(): boolean {
    return this.stepPosition === 1;
  }

  private get allowedCookies(): boolean {
    const cookie = new Cookie({ name: 'COOKIE_EMP_ALLOW_COOKIES' });
    return cookie.value === 'allow';
  }

  private goToLogin() {
    this.$router.push({
      name: 'checkout',
      query: this.$router.currentRoute.query,
    });
  }

  private validateFirstStep() {
    return new Promise(async (resolve: any, reject) => {
      const result = await this.validator.validateAll({
        ...this.form.essencialInformation,
      });

      result ? resolve() : reject();
    });
  }

  private validateForm() {
    return new Promise(async (resolve: any, reject) => {
      const result = await this.validator.validateAll({
        ...this.form.address,
        ...this.form.essencialInformation,
      });

      if (!this.form.address.zipCodeData) {
        if (this.form.address.postal_code.length >= 8) {
          this.validator.validate('postal_code', '0');
        } else {
          this.validator.validate('postal_code', this.form.address.postal_code.replace(/\D/g, ''));
        }
      }

      result ? resolve() : reject();
    });
  }

  private goToFirstStep() {
    this.stepPosition = 0;
  }

  private zipCodeNotFound(): void {
    const { name, email, phone_numbers } = this.customer;
    const phone = phone_numbers && phone_numbers[0].phone;
    const { postal_code } = this.form.address;
    const newCustomer: IZipCodeNotFound = {
      name,
      email,
      phone: getValueIfIsNotNil(phone),
      zipCode: postal_code
    };
    // this.notFoundZipCode(newCustomer);
  }

  private async goToSecondStep() {
    try {
      this.isLoadingFirstStep = true;
      await this.validateFirstStep();
      this.stepPosition = 1;
      this.isLoadingFirstStep = false;
    } catch (e) {
      this.isLoadingFirstStep = false;
      console.error(e);
    }
  }

  private async submitForm() {
    try {
      await this.validateForm();
      this.signup({
        customer: this.customer,
        isEverywhere: false,
      });
    } catch (e) {
      console.error(e);
    }
  }

  private mounted() {
    this.addObserver(this.setHeight, document.body);

    executeCaptcha('register')
      .then((success: boolean) => {
        this.setCaptchaValid(success);
      });
  }

  setHeight(): void {
    const { step1, step2 } = this.$refs;
    if (this.stepPosition === 0 && step1)
      this.stepHeight = step1.clientHeight ? `${step1.clientHeight}px` : 'auto';
    else if (this.stepPosition === 1 && step2)
      this.stepHeight = step2.clientHeight ? `${step2.clientHeight}px` : 'auto';
    else this.stepHeight = 'auto';
  }

  private destroyed() {
    this.validator.reset();
    this.clearErrors();
  }
}
